import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { API_BASE, PROJECT } from '../../constants/general.constants';
import { ConnectionType } from '../../interfaces';
import { AppStateService } from '../app-state/app-state.service';
import { ConnectionService } from '../connection/connection.service';
import type { StepParams } from '@enlighten/ts-shared/step-api';

interface DeletePrefixResult {
  prefix: string;
  deleted: number;
}

interface SignedUrlResult {
  url: string;
  path: string;
  method: string;
  expirySecs: number;
}

/**
 * Object-storage operations (delete and presigned URLs) exposed to workflow
 * step code (delete: SCRUM-781, presign: EHQ-5).
 *
 * Newton-rs (Arrow Flight) has no delete action — it is a compute service, not a
 * file-management proxy — so delete is surfaced here by calling ehq-server's
 * existing object-storage HTTP delete endpoints. Connection names are resolved
 * to ids locally from the active project's connections (the endpoints are keyed
 * by connection id, but step code passes a connection name).
 *
 * Registered under the ARROW_FLIGHT namespace (alongside listCloud) via
 * step-bindings.ts; bridge methods take the shared parser's params (EHQ-10) and
 * return a Promise, matching the other async bridge services.
 *
 * presignUrl (EHQ-5) is likewise routed here — to ehq-server's HTTP signed-url
 * endpoint — instead of to newton-rs, which required an already-open Arrow
 * Flight session and failed silently when presign was a step's first Flight op.
 */
@Injectable({
  providedIn: 'root'
})
export class ObjectStorageFileService {
  readonly #httpClient = inject(HttpClient);
  readonly #appState = inject(AppStateService);
  readonly #connectionService = inject(ConnectionService);

  /**
   * Resolve an ObjectStorage connection name to its numeric id from the active
   * project's connections. Throws if no matching connection exists.
   */
  #resolveConnectionId(connectionName: string): number {
    if (!connectionName) {
      throw new Error('ObjectStorage connection name must be provided');
    }
    const connection = this.#connectionService
      .connections()
      .find(c => c.name === connectionName && c.type === ConnectionType.ObjectStorage);
    if (!connection?.connectionId) {
      throw new Error(`ObjectStorage connection "${connectionName}" not found`);
    }
    return connection.connectionId;
  }

  #filesUrl(connectionId: number): string {
    const projectId = this.#appState.projectId();
    if (!projectId) {
      throw new Error('No active project; cannot delete object-storage files');
    }
    return `${API_BASE}/${PROJECT}/${projectId}/object-storage/${connectionId}/files`;
  }

  /**
   * Delete a single object.
   */
  async deleteCloudObject(params: StepParams['ARROW_FLIGHT.deleteCloudObject']): Promise<void> {
    const connectionId = this.#resolveConnectionId(params.connectionName);
    const httpParams = new HttpParams().set('path', params.path);
    await firstValueFrom(
      this.#httpClient.delete<void>(this.#filesUrl(connectionId), { params: httpParams })
    );
  }

  /**
   * Delete every object under a prefix. Returns the number of objects deleted.
   * The parser requires a non-empty prefix, so this can't wipe the whole
   * connection.
   */
  async deleteCloudPrefix(params: StepParams['ARROW_FLIGHT.deleteCloudPrefix']): Promise<number> {
    const connectionId = this.#resolveConnectionId(params.connectionName);
    const httpParams = new HttpParams().set('prefix', params.prefix);
    const result = await firstValueFrom(
      this.#httpClient.post<DeletePrefixResult>(
        `${this.#filesUrl(connectionId)}/delete-prefix`,
        null,
        { params: httpParams }
      )
    );
    return result?.deleted ?? 0;
  }

  /**
   * Generate a presigned URL for an object via ehq-server's HTTP signed-url
   * endpoint (EHQ-5). Unlike the former newton-rs SignResource DoAction, this
   * needs no active Arrow Flight session, so a step can presign as its very
   * first Flight operation.
   * Returns the presigned URL string (same return shape as the old WS path).
   */
  async presignUrl(params: StepParams['ARROW_FLIGHT.presignUrl']): Promise<string> {
    const { connectionName, path, expirySecs, method } = params;
    const connectionId = this.#resolveConnectionId(connectionName);
    let httpParams = new HttpParams().set('path', path).set('method', method);
    // Only forward a positive expiry; a 0/negative value would presign an
    // already-expired URL. Omitting it lets the server apply its default. The
    // parser already drops one, but in-app callers build params themselves.
    if (expirySecs !== undefined && expirySecs > 0) {
      httpParams = httpParams.set('expirySecs', String(expirySecs));
    }
    const result = await firstValueFrom(
      this.#httpClient.get<SignedUrlResult>(
        `${this.#filesUrl(connectionId)}/signed-url`,
        { params: httpParams }
      )
    );
    if (!result?.url) {
      throw new Error(
        `Presign for "${path}" on "${connectionName}" returned no URL`
      );
    }
    return result.url;
  }
}
